import type { default as LottieComponent, LottiePlayer } from 'lottie-react';
import React from 'react';

import { useThemeVariants } from '@gitbook/react-uikit';
import {
    createCacheSingleton,
    executeSuspenseAll,
    type SuspenseCacheSingleton,
} from '@gitbook/util-suspense-cache';

export type AnimatedLottieSource = {
    dark: () => Promise<{ default: unknown }>;
    light: () => Promise<{ default: unknown }>;
};

export type AnimatedLottieProps = {
    /**
     * Source of the animation.
     * Pass dynamic imports to avoid loading the animation data until the component is mounted.
     */
    src: {
        dark: SuspenseCacheSingleton<null, unknown>;
        light: SuspenseCacheSingleton<null, unknown>;
    };

    /**
     * Size of the icon.
     */
    size: number;

    /**
     * Whether to loop the animation.
     * @default true
     */
    loop?: boolean;
};

/**
 * Create a lazy loaded source of an animated icon.
 */
function createAnimatedLottieSource(source: AnimatedLottieSource): AnimatedLottieProps['src'] {
    return {
        dark: createCacheSingleton(async () => {
            const module = await source.dark();
            return module.default;
        }),
        light: createCacheSingleton(async () => {
            const module = await source.light();
            return module.default;
        }),
    };
}

/**
 * Create an animated icon.
 */
export function createAnimatedLottie(source: AnimatedLottieSource) {
    const src = createAnimatedLottieSource(source);
    return (props: Omit<AnimatedLottieProps, 'src'>) => <AnimatedLottie {...props} src={src} />;
}

/**
 * Lazy load and render an animated icon.
 */
function AnimatedLottie(props: AnimatedLottieProps) {
    const { size } = props;

    return (
        <React.Suspense fallback={<div style={{ width: size, height: size }} />}>
            <AnimatedLottieLoader {...props} />
        </React.Suspense>
    );
}

const lazyLottie = createCacheSingleton(async () => {
    const module = await import('lottie-react');

    // @ts-ignore
    return module.default.default as LottieComponent;
});

function AnimatedLottieLoader(props: AnimatedLottieProps) {
    const { src, size, loop = true } = props;
    const variants = useThemeVariants();

    const lottieRef = React.useRef<typeof LottiePlayer>(null);

    const [Lottie, animationData] = executeSuspenseAll([
        () => lazyLottie.read(null),
        () => (variants.color === 'dark' ? src.dark.read(null) : src.light.read(null)),
    ] as const);

    return (
        <Lottie
            lottieRef={lottieRef}
            autoplay={!process.env.GITBOOK_PREDICTABLE_UI}
            animationData={animationData}
            loop={loop}
            style={{ width: size, height: size }}
            onClick={() => {
                // @ts-expect-error - it's actually a valid function https://lottiereact.com/components/Lottie#gotoandplayvalue-isframe
                lottieRef.current?.goToAndPlay(0, false);
            }}
        />
    );
}
